// Application bootstrap (§4). Orchestrator only: it reads the registry, resolves the
// page + language, then delegates injection/translation/behavior to the managers.

import I18nCore from '/core/managers/system/I18nCore.js';
import ComponentManager from '/core/managers/system/ComponentManager.js';
import PageRouter from '/core/managers/system/PageRouter.js';
import { detectLang } from '/shared/utils/core/url.js';
import { initPageLinks } from '/shared/utils/core/pageLinks.js';
import { loadManager } from '/core/managers/pages/loadManager.js';
import { initDevisSync } from '/shared/utils/core/devisSync.js';
import { startFreshFunnelEntry } from '/shared/utils/core/devisRecord.js';
import { followKnownRecords } from '/shared/utils/core/recordSync.js';
import { injectHtml } from '/shared/utils/core/dom.js';
import { initMotion } from '/shared/utils/core/motion.js';
import { trackOnce } from '/shared/analytics/track.js';
import { initClarity } from '/shared/analytics/clarity.js';
import { initProspection } from '/shared/analytics/prospection.js';
import { arrivalGesture } from '/shared/analytics/arrival.js';
import { initClickTrail } from '/shared/analytics/clicks.js';
import { scheduleIdle, loadStyles } from '/shared/utils/performance/index.js';
import { initScrollObserver } from '/shared/utils/performance/index.js';
import { applyDeviceOptimizations } from '/shared/utils/performance/index.js';

async function bootstrap() {
  const registry = window.PAGE_REGISTRY;
  if (!registry) {
    console.error('[main] window.PAGE_REGISTRY is missing — did generate:config run?');
    return;
  }

  const { site } = registry;
  const lang = detectLang(location.pathname, site);
  document.documentElement.lang = site.locales[lang] || lang;

  const router = new PageRouter(registry);
  const page = router.resolve(location.pathname, lang);
  if (!page) {
    console.error(`[main] no page matches "${location.pathname}" (${lang})`);
    return;
  }

  // A FINISHED journey does not greet the next one. Opening the estimate funnel with a
  // reserved devis still in storage starts a NEW quote, so the old record is dropped here —
  // before anything below can measure — or every gesture of the new visit would ride the
  // finished devis, and the admin saw the old prospect stir at the first typed letter
  // (owner-reported 2026-10-28; devisRecord.js carries the rest of the reasoning). Not on a
  // capture link: a QR carries the devis it wants filmed, and that devis is the subject.
  if (page.key === 'estimate' && !new URLSearchParams(location.search).has('devis')) {
    startFreshFunnelEntry();
  }

  // The funnel's first step, and it must be the FIRST event of the journey — before the
  // sections and their managers, which report steps of their own (the quote page prices as it
  // opens). Once per journey: the client space and the marketing pages are not the funnel, and
  // re-entering the capture step is not a new visit.
  if (page.analytics) trackOnce('tunnel_debut', { langue: lang });

  // The journey the admin's Prospection page reads (docs/prospection-evenements.md). Wired
  // before anything else measures, for the same reason as the line above: the sections report
  // steps of their own, and they must not land before the arrival.
  if (page.analytics) {
    // WHERE they pressed, which the journey alone could never say (shared/analytics/
    // clicks.js). Gated by the same `analytics` flag as everything above: the client space is
    // not the funnel, and someone reading their own invoices is not a prospect being measured.
    //
    // ⚠ BEFORE `initProspection`, AND THE ORDER IS THE WHOLE POINT. Both listen for
    // `pagehide` to send what they are still holding, and listeners run in the order they
    // were added: the journal registered first would flush the queue, and the last batch of
    // clicks would be pushed into a queue that had already left. Wired first, its points are
    // in the queue before the queue goes.
    initClickTrail();
    initProspection();
    // A VISIT OR A RETURN, and the devis decides. Someone whose record already carries an id
    // is coming BACK — from an email, a shared quote, their own history. Counting that as a
    // first visit would make every funnel look re-entered, and lose the moment the campaign
    // actually brought them.
    arrivalGesture(lang);
  }

  const i18n = new I18nCore({ lang });
  const components = new ComponentManager({ i18n, registry });

  // Fill the SEO head (title/meta) and the body SKELETON (skip link, shell chrome such
  // as the space drawer button) — the containers are still empty at this point, so the
  // pass is cheap; injected sections are translated at injection below.
  await i18n.apply(document.head);
  await i18n.apply(document.body);

  // Build the critical path: header + section HTML in parallel.
  await Promise.all([components.load('header'), injectSections(page, i18n)]);

  // Page manager wires section behavior on the already-injected DOM. A fully static page
  // (no behavior) declares no manager — skip rather than ship an empty one (golden rule #8).
  // The resolved registry entry is passed along so a manager can act on page config
  // (e.g. promote deferred styles when a deep-link reveals below-the-fold sections).
  // Contained on purpose: a manager that throws must not take the rest of bootstrap with it.
  // Everything below — deferred styles among them — would otherwise never run, leaving the
  // page rendered without its section stylesheets, which reads as a broken layout rather than
  // as the broken script it is.
  if (page.manager) {
    try {
      const module = await loadManager(page.manager);
      await module?.default.init(page);
    } catch (err) {
      console.error(`[main] page manager "${page.manager}" failed:`, err);
    } finally {
      // The capture arrival paints the page black until its manager decides what to show
      // (html-assembler.js). A manager that threw would leave it black forever — worse than
      // whatever it failed to wire — so the attribute goes whatever happened. Removing an
      // absent attribute costs nothing, and every other page never carries it.
      document.documentElement.removeAttribute('data-capture-boot');
    }
  }

  // Animations on, now that the page has its first paint: what the user does from here
  // moves, what was already there does not (shared/utils/core/motion.js).
  initMotion();

  // Resolve cross-page CTA links (header + sections) from the registry slugs.
  initPageLinks(document);

  // Mirror the devis record to the platform on every change. Wired here rather than in each
  // page manager: the record spans the marketing funnel AND the app pages, and a manager that
  // forgot the call would drop the client's answers silently.
  initDevisSync();

  // Name the Clarity recording after the devis, so the admin can open it from the prospect's
  // file. Asked of the registry like the banner below: only the funnel carries the tag, and
  // only the funnel has a devis to name it after.
  if (page.analytics) initClarity();

  // Follow the records already stored, so a reloaded page rejoins its rooms. Adoption alone
  // only covers the visit that created them; after that the ids come from storage and nothing
  // would subscribe.
  followKnownRecords();

  // The consent banner, on the pages that carry the measurement tag — asked of the registry
  // (`analytics: true`), the very flag the server reads to inject that tag, so the two can
  // never disagree about which pages are the funnel. Not critical: it is an overlay, and the
  // tag it governs has already denied everything until it answers.
  if (page.analytics) components.load('consent');

  // Below the fold and non-critical work.
  components.load('footer');
  applyDeviceOptimizations();
  scheduleIdle(() => loadStyles(page.deferredStyles || []));
  scheduleIdle(() => initScrollObserver());
}

/**
 * Inject the HTML of every visible section, then translate each.
 * Single fetch+inject implementation (golden rule #1).
 *
 * IN PARALLEL, not in registry order: each section writes into its OWN container, so
 * nothing depends on the order they land in. Serially, a page declaring eight sections
 * paid eight round trips end to end before it could paint — and on the client space
 * seven of those eight are views the client is not looking at.
 */
async function injectSections(page, i18n) {
  const sections = (page.sections || []).filter((s) => s.runtime && !s.hidden);
  await Promise.all(
    sections.map(async (section) => {
      const container = document.querySelector(section.target);
      if (!container) return;
      try {
        const res = await fetch(section.html);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        injectHtml(container, await res.text());
        await i18n.apply(container);
      } catch (err) {
        console.error(`[main] failed to inject section "${section.name}":`, err.message);
      }
    })
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}
